import { motion } from "framer-motion";
import { Trash2, Smartphone, Mail } from "lucide-react";
import { usePageMeta } from "@/hooks/use-page-meta";

const Step = ({ icon: Icon, title, children, testId }) => (
  <section
    data-testid={testId}
    className="mt-8 rounded-lg border border-black/10 bg-white p-8"
  >
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0A0A0A] text-white">
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="font-heading text-lg font-bold tracking-tight text-[#0A0A0A]">
        {title}
      </h2>
    </div>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#4B5563] md:text-base">
      {children}
    </div>
  </section>
);

export default function DataDeletion() {
  usePageMeta(
    "Data Deletion — Bright Frontier Labs",
    "How to delete your Mu'min Kids parent account and local learning data, or request deletion of store-linked records from Bright Frontier Labs."
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="data-deletion-page"
    >
      <div className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-3xl px-6 py-20 md:px-8 md:py-24">
          <span className="text-xs font-semibold uppercase tracking-widest text-white/50">
            Legal &mdash; 03
          </span>
          <h1
            data-testid="data-deletion-heading"
            className="mt-6 font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Data Deletion
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/50 md:text-base">
            How to delete your Mu&rsquo;min Kids parent account, clear local
            learning data, or request deletion of store-linked records.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-16 md:px-8 md:py-20">
        <p className="text-sm leading-relaxed text-[#4B5563] md:text-base">
          Mu&rsquo;min Kids keeps children&rsquo;s learning data on the device
          by design, so most deletion is instant and fully in your hands.
          Here are the three ways to delete data associated with the app.
        </p>

        <Step
          icon={Trash2}
          title="Delete your parent account (in the app)"
          testId="data-deletion-account-section"
        >
          <p>
            In the app, go to the{" "}
            <strong className="text-[#0A0A0A]">Parent tab</strong> &rarr;{" "}
            <strong className="text-[#0A0A0A]">Parent Account</strong> &rarr;{" "}
            <strong className="text-[#0A0A0A]">Delete Parent Account</strong>.
          </p>
          <p>
            This deletes the optional parent account and the sign-in
            information associated with it (such as the parent display name,
            email address, phone number, and authentication UID held by
            Firebase Authentication). This action is immediate and cannot be
            undone.
          </p>
        </Step>

        <Step
          icon={Smartphone}
          title="Clear local learning data (on the device)"
          testId="data-deletion-local-section"
        >
          <p>
            In the app, go to the{" "}
            <strong className="text-[#0A0A0A]">Parent tab</strong> &rarr;{" "}
            <strong className="text-[#0A0A0A]">Privacy &amp; Data</strong>{" "}
            &rarr;{" "}
            <strong className="text-[#0A0A0A]">Clear Learning Data</strong>.
          </p>
          <p>
            This removes everything stored on the device: child nicknames, age
            groups, avatars, learning progress, rewards, streaks, settings,
            locally scheduled reminders, and temporary pronunciation
            recordings. Uninstalling the app also removes this local data.
            Because this data never leaves the device, it remains there unless
            you clear it separately.
          </p>
        </Step>

        <Step
          icon={Mail}
          title="Request store-linked deletion (by email)"
          testId="data-deletion-email-section"
        >
          <p>
            For eligible deletion requests involving subscription records
            linked to RevenueCat or the app stores, email us at{" "}
            <a
              href="mailto:support@brightfrontierlabs.com"
              data-testid="data-deletion-mailto-link"
              className="font-medium text-[#0A0A0A] underline decoration-black/30 underline-offset-4 transition-colors duration-200 hover:decoration-black"
            >
              support@brightfrontierlabs.com
            </a>{" "}
            and tell us what you would like deleted. We will confirm and
            process eligible requests.
          </p>
          <p>
            We will never ask for your password or payment details by email
            &mdash; please do not send them.
          </p>
        </Step>

        <section
          data-testid="data-deletion-retention-section"
          className="mt-10 border-t border-black/10 pt-8"
        >
          <h2 className="font-heading text-lg font-bold tracking-tight text-[#0A0A0A]">
            What may be retained
          </h2>
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#4B5563] md:text-base">
            <p>
              Purchase and subscription records held by Google Play, Apple, or
              RevenueCat may be retained where legally required (for example,
              tax and accounting rules) or where needed to restore purchases
              and prevent fraud. Everything else &mdash; your child&rsquo;s
              learning world &mdash; stays on your device and is yours to clear
              at any time.
            </p>
          </div>
        </section>
      </div>
    </motion.div>
  );
}
